<template>
  <!-- Q4 "Recent activity" from the Work Progress design: tasks by last progress update,
       newest first, with actual progress against the planned marker -->
  <v-card class="recent-card">
    <div class="head">
      <h2 class="card-title">{{ $t('taskRecent.title') }}</h2>
      <div class="legend">
        <span><span class="swatch swatch--bar"></span>{{ $t('taskSCurve.actual') }}</span>
        <span v-if="!isLite"><span class="swatch swatch--tick"></span>{{ $t('taskSCurve.planned') }}</span>
      </div>
    </div>

    <v-skeleton-loader v-if="!rows.length && loading" type="table-tbody" />
    <div v-else-if="!rows.length" class="empty text-medium-emphasis">{{ $t('taskSCurve.noProgress') }}</div>

    <div v-else class="scroll">
      <table class="tbl">
        <thead>
          <tr>
            <th>{{ $t('taskRecent.task') }}</th>
            <th class="center">{{ $t('taskRecent.updated') }} <v-icon size="10">mdi-menu-down</v-icon></th>
            <th class="progress-col">{{ $t('taskRecent.progress') }}</th>
            <th v-if="!isLite" class="center">{{ $t('taskRecent.dates') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.taskId"
            class="row--clickable"
            :class="{ 'is-selected': selected === row.taskId, 'is-dimmed': selected && selected !== row.taskId }"
            role="button"
            tabindex="0"
            :aria-pressed="selected === row.taskId"
            @click="toggleRecentTaskFilter(row.taskId)"
            @keydown.enter.prevent="toggleRecentTaskFilter(row.taskId)"
          >
            <td class="name-cell">
              <div class="name" :title="row.taskTitle">{{ row.taskTitle }}</div>
              <div class="path" :title="row.path">{{ row.path }}</div>
            </td>
            <td class="center nowrap">
              <div>{{ row.updatedRel }}</div>
              <div class="sub">{{ row.updated }}</div>
            </td>
            <td>
              <div class="bar-row">
                <div class="track">
                  <div class="fill" :style="{ width: `${row.actual}%`, background: row.behind ? '#F5A623' : '#4CAF50' }"></div>
                  <div v-if="!isLite && row.planned !== null" class="marker" :style="{ left: `${row.planned}%` }"></div>
                </div>
                <span class="pct" :class="row.behind ? 'tk-warn' : ''">
                  {{ row.actual }}%<span v-if="!isLite && row.planned !== null" class="plan"> / {{ row.planned }}%</span>
                </span>
              </div>
            </td>
            <td v-if="!isLite" class="center nowrap sub">{{ row.start }} → {{ row.end }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { recentActivitySql } from '@/utils/taskTables';
import { taskSCurveFilters, taskScopeKey, taskScope, isTaskScopeReady, toggleRecentTaskFilter } from '@/utils/taskSCurveFilters';
import { formatDayMonth, formatShortDate } from '@/utils/taskDates';

const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const locale = computed(() => store.getters.currentLocale);

const raw = ref([]);
const loading = ref(false);
const scheduleMode = ref('sync');
const isLite = computed(() => scheduleMode.value === 'lite');

let queryVersion = 0;

const queryRows = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray().map(row => row.toJSON());
};

const loadData = async () => {
  if (!dataLoaded.value || !isTaskScopeReady.value) return;
  const version = ++queryVersion;
  loading.value = true;
  try {
    // Ignore the recent-activity cross-filter here: this table is where it is picked
    const result = await queryRows(recentActivitySql({
      ...taskScope(['recent']),
      limit: 50,
    }));
    if (version !== queryVersion) return; // a newer request superseded this one
    raw.value = result;
    if (result.length) scheduleMode.value = result[0].scheduleMode ?? 'sync';
  } catch (error) {
    if (version !== queryVersion) return;
    console.error('[TaskRecentActivityCard] query failed:', error.message);
    raw.value = [];
  } finally {
    if (version === queryVersion) loading.value = false;
  }
};

// Clicking a row cross-filters the other visuals to that task
const selected = computed(() => taskSCurveFilters.recentTaskIds[0] ?? null);

const relative = (daysAgo) => {
  if (daysAgo <= 0) return t('taskRecent.today');
  if (daysAgo === 1) return t('taskRecent.yesterday');
  return t('taskRecent.daysAgo', { n: daysAgo });
};

const rows = computed(() => raw.value.map((r) => {
  const actual = Math.min(100, Math.round(Number(r.actualPct ?? 0)));
  const planned = r.plannedPct === null || r.plannedPct === undefined ? null : Math.round(Number(r.plannedPct));
  return {
    taskId: r.taskId,
    taskTitle: r.taskTitle,
    path: r.path ?? '',
    updatedRel: relative(Number(r.updatedDaysAgo ?? 0)),
    updated: formatShortDate(r.updatedOn, locale.value),
    actual,
    planned,
    // Below plan and not finished → amber bar and amber figure
    behind: !isLite.value && !r.completed && planned !== null && actual < planned,
    start: formatDayMonth(r.plannedStartDate, locale.value),
    end: formatDayMonth(r.plannedEndDate, locale.value),
  };
}));

onMounted(loadData);
watch(
  [dataLoaded, taskScopeKey],
  loadData,
  { deep: true }
);
</script>

<style scoped>
.recent-card {
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0 8px;
}
.head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
}
.card-title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}
.legend {
  display: flex;
  gap: 14px;
  font-size: 12px;
  opacity: 0.85;
}
.legend > span {
  display: flex;
  align-items: center;
  gap: 6px;
}
.swatch--bar {
  width: 14px;
  height: 6px;
  border-radius: 3px;
  background: #4CAF50;
  display: inline-block;
}
.swatch--tick {
  width: 3px;
  height: 12px;
  border-radius: 2px;
  background: var(--tk-info);
  display: inline-block;
}
.empty {
  padding: 8px 20px 12px;
  font-size: 14px;
}
.scroll {
  overflow: auto;
  max-height: 300px;
  padding: 0 8px;
}
.tbl {
  border-collapse: collapse;
  width: 100%;
  min-width: 560px;
  font-size: 13px;
}
.tbl th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
  text-align: left;
  padding: 10px 12px;
  font-weight: 500;
  font-size: 13px;
  opacity: 0.75;
  border-bottom: 1px solid var(--tk-divider);
  white-space: nowrap;
}
.tbl td {
  padding: 7px 12px;
  border-bottom: 1px solid var(--tk-divider);
  vertical-align: middle;
}
.tbl tr:hover td {
  background: var(--tk-row-hover);
}
.row--clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.row--clickable.is-selected td {
  background: var(--tk-row-hover);
  box-shadow: inset 3px 0 0 var(--tk-info);
}
.row--clickable.is-dimmed {
  opacity: 0.45;
}
.center {
  text-align: center;
}
.nowrap {
  white-space: nowrap;
}
.progress-col {
  min-width: 180px;
}
.name-cell {
  min-width: 0;
  max-width: 220px;
}
.name {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.path,
.sub {
  font-size: 11px;
  opacity: 0.55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.track {
  position: relative;
  flex: 1 1 auto;
  height: 8px;
  border-radius: 4px;
  background: var(--tk-track);
}
.fill {
  height: 8px;
  border-radius: 4px;
}
.marker {
  position: absolute;
  top: -3px;
  width: 3px;
  height: 14px;
  margin-left: -1.5px;
  border-radius: 2px;
  background: var(--tk-info);
}
.pct {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
}
.plan {
  opacity: 0.5;
}
</style>
